import React, { useState, useEffect } from 'react';
import api from '../api/api';

const FIELD_STYLE = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-sap-blue focus:border-sap-blue outline-none';
const LABEL_STYLE = 'block text-sm font-medium text-gray-700 mb-1';

export default function EmailSettings() {
  const [form, setForm] = useState({
    email_enabled: 'false',
    email_resend_api_key: '',
    email_from: '',
    email_to: '',
    email_smtp_host: '',
    email_smtp_port: '',
    email_smtp_user: '',
    email_smtp_pass: '',
    email_to_roles: 'Super Admin,Admin',
    notify_overdue_days: '30',
  });
  // Who the daily notices actually reach, worked out by the server from the roles
  // and the address list together. A list of typed addresses does not answer that
  // on its own, and getting it wrong means somebody quietly stops being told.
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [flash, setFlash] = useState(null);

  const loadRecipients = () => {
    api.get('/settings/email/recipients')
      .then(setResolved)
      .catch(() => setResolved(null));
  };

  useEffect(() => {
    api.get('/settings/email').then(data => {
      setForm(prev => ({ ...prev, ...data }));
    }).catch(() => {}).finally(() => setLoading(false));
    loadRecipients();
  }, []);

  const showFlash = (msg, type = 'success') => {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 5000);
  };

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await api.put('/settings/email', form);
      setForm(prev => ({ ...prev, ...data }));
      loadRecipients();
      showFlash('Email settings saved successfully');
    } catch (err) {
      showFlash(err.message || 'Failed to save settings', 'error');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await api.post('/settings/email/test');
      showFlash('Test email sent! Check recipient inbox.');
    } catch (err) {
      showFlash(err.message || 'Failed to send test email', 'error');
    } finally { setTesting(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-6 w-6 border-2 border-sap-blue border-t-transparent rounded-full" />
      </div>
    );
  }

  const enabled = form.email_enabled === 'true';

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Email Notification Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Resend or SMTP (saved in database). Save after pasting your API key.</p>
      </div>

      {flash && (
        <div className={`mb-4 px-4 py-3 rounded-md text-sm font-medium ${
          flash.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {flash.msg}
        </div>
      )}

      {/* Enable toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Email Notifications</h2>
            <p className="text-sm text-gray-500">Send email alerts when material delay notifications are generated</p>
          </div>
          <button
            onClick={() => handleChange('email_enabled', enabled ? 'false' : 'true')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-sap-blue' : 'bg-gray-300'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {/* SMTP Configuration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-5">
        <h2 className="text-base font-semibold text-gray-800 mb-4">SMTP Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_STYLE}>SMTP Host</label>
            <input className={FIELD_STYLE} placeholder="smtp.hostinger.com" value={form.email_smtp_host || ''}
              onChange={e => handleChange('email_smtp_host', e.target.value)} />
          </div>
          <div>
            <label className={LABEL_STYLE}>SMTP Port</label>
            <input className={FIELD_STYLE} placeholder="465" value={form.email_smtp_port || ''}
              onChange={e => handleChange('email_smtp_port', e.target.value)} />
          </div>
          <div>
            <label className={LABEL_STYLE}>SMTP User (Username)</label>
            <input className={FIELD_STYLE} placeholder="web@anvenssa.com" value={form.email_smtp_user || ''}
              onChange={e => handleChange('email_smtp_user', e.target.value)} />
          </div>
          <div>
            <label className={LABEL_STYLE}>SMTP Password</label>
            <input className={FIELD_STYLE} type="password" placeholder="••••••••" value={form.email_smtp_pass || ''}
              onChange={e => handleChange('email_smtp_pass', e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className={LABEL_STYLE}>From Email Address</label>
            <input className={FIELD_STYLE} placeholder="web@anvenssa.com" value={form.email_from || ''}
              onChange={e => handleChange('email_from', e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">The email address that the notifications will be sent from (must be allowed by your SMTP provider).</p>
          </div>
        </div>
      </div>

      {/* Recipient Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-5">
        <h2 className="text-base font-semibold text-gray-800 mb-1">Who receives the daily notices</h2>
        <p className="text-sm text-gray-500 mb-4">
          The 09:00 summary of production and purchase orders that are due or overdue.
          Approval emails are not affected: those always go to the approver named on the document.
        </p>

        <div className="mb-4">
          <label className={LABEL_STYLE}>By role</label>
          <div className="flex flex-wrap gap-3">
            {(resolved?.available_roles || ['Super Admin', 'Admin', 'Manager', 'Viewer']).map((role) => {
              const picked = String(form.email_to_roles || '')
                .split(',').map((r) => r.trim()).filter(Boolean);
              const on = picked.includes(role);
              return (
                <label key={role} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => {
                      const next = on ? picked.filter((r) => r !== role) : [...picked, role];
                      handleChange('email_to_roles', next.join(','));
                    }}
                    className="rounded border-gray-300"
                  />
                  {role}
                </label>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Everyone active in a ticked role receives the notices. A new colleague in that role
            is included automatically — nothing to edit here.
          </p>
        </div>

        <div>
          <label className={LABEL_STYLE}>Also send to these addresses</label>
          <input className={FIELD_STYLE} placeholder="shared.inbox@company.com, someone@company.com"
            value={form.email_to} onChange={e => handleChange('email_to', e.target.value)} />
          <p className="text-xs text-gray-400 mt-1">
            Comma-separated. For mailboxes that do not belong to a user with a login. Leave empty
            if the roles above cover everyone.
          </p>
        </div>

        {resolved && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <div className="text-xs font-medium text-gray-600 mb-1.5">
              {resolved.total === 0
                ? 'Nobody would receive these emails'
                : `${resolved.total} recipient${resolved.total === 1 ? '' : 's'} right now`}
            </div>
            {resolved.total === 0 ? (
              <p className="text-xs text-red-600">
                Tick a role, or add an address above, then save.
              </p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {resolved.recipients.map((r) => (
                  <li key={r.email}
                    className="text-xs bg-gray-50 border border-gray-200 rounded px-2 py-0.5 text-gray-700">
                    {r.email}
                    <span className="text-gray-400 ml-1">
                      {r.source === 'role' ? `· ${r.role_name}` : '· added here'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-400 mt-2">Updated when you save.</p>
          </div>
        )}
      </div>

      {/* How much to report */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-5">
        <h2 className="text-base font-semibold text-gray-800 mb-4">What the daily notices cover</h2>
        <div className="max-w-xs">
          <label className={LABEL_STYLE}>Report items overdue by up to</label>
          <div className="flex items-center gap-2">
            <input className={FIELD_STYLE} type="number" min="0" max="3650"
              value={form.notify_overdue_days}
              onChange={e => handleChange('notify_overdue_days', e.target.value)} />
            <span className="text-sm text-gray-500 whitespace-nowrap">days</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Anything older is counted in one line rather than listed. Without this the summary
            ran to hundreds of orders left open in SAP years ago, which buried the few that
            are actually slipping. Set 0 to list everything.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving}
          className="px-5 py-2.5 bg-sap-blueDark hover:bg-sap-blueDeeper text-white text-sm font-medium rounded-md disabled:opacity-50 transition-colors">
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
        <button onClick={handleTest} disabled={testing || !form.email_from || resolved?.total === 0}
          className="px-5 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md disabled:opacity-50 transition-colors">
          {testing ? 'Sending…' : 'Send Test Email'}
        </button>
      </div>

      {/* Help section */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-800 mb-2">Setup Guide</h3>
        <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
          <li>The server runs a <strong>daily job at 9:00 (Asia/Kolkata)</strong> to refresh production due reminders and send the digest email — no login required. Disable with env <code className="bg-blue-100 px-1 rounded">PRODUCTION_REMINDER_CRON=off</code> or change time via <code className="bg-blue-100 px-1 rounded">PRODUCTION_REMINDER_CRON</code> / <code className="bg-blue-100 px-1 rounded">PRODUCTION_REMINDER_TZ</code>.</li>
          <li>Configure the <strong>SMTP Configuration</strong> section above with your email server details (e.g. Hostinger, Google Workspace, Office 365, etc.).</li>
          <li>Provide a valid <strong>From Email Address</strong> that matches your SMTP account settings.</li>
          <li>Add <strong>Recipient</strong> email addresses in the Recipients section to receive alerts.</li>
          <li>Click <strong>Send Test Email</strong> to verify your configuration.</li>
        </ol>
      </div>
    </div>
  );
}
